// Password-reset calls. Login/refresh/logout live in stores/agent.ts because
// they mutate session state; these three are stateless and used by the two
// unauthenticated pages, so they sit here instead of bloating the store.

import { apiFetch } from './api';

/**
 * Ask for a reset link. Always resolves — the server answers `{ok:true}` for
 * unknown addresses too, so the UI can never be used to discover which emails
 * have accounts. Treat a rejection as "couldn't reach the server", nothing more.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await apiFetch<{ ok: true }>('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/**
 * Is the emailed link still usable? Decides whether to show the form.
 * POST for a read, deliberately — a token in a query string ends up in access
 * logs and Referer headers.
 */
export async function checkResetToken(token: string): Promise<boolean> {
  const r = await apiFetch<{ valid: boolean }>('/api/auth/reset-password/check', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
  return r.valid;
}

/** Spend the link and set the new password. Signs out every existing session. */
export async function resetPassword(token: string, password: string): Promise<void> {
  await apiFetch<{ ok: true }>('/api/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

/** Shared with the server's zod validator — keep the two in step. */
export const MIN_PASSWORD_LENGTH = 8;

// ─── Self-serve signup ───

export interface SignupInput {
  name: string;
  email: string;
  password: string;
  workspaceName?: string;
  website?: string;
  acceptTerms: boolean;
}

/** Per-field problem codes from the server's validator, e.g.
 *  `{ password: 'too-short', website: 'invalid-website' }`. */
export type SignupFieldErrors = Record<string, string>;

/**
 * Create an account. Resolves for every accepted request — including an
 * address that already has an account, which the server deliberately answers
 * the same way — and the next step is always "check your email". Rejects with
 * the ApiError for a field problem (400, `body.fields`), 429 or no network.
 */
export async function signup(input: SignupInput): Promise<void> {
  await apiFetch<{ ok: true }>('/api/auth/signup', { method: 'POST', body: JSON.stringify(input) });
}

/** Ask for another verification link. Always resolves the same way, whether
 *  or not the address is waiting to be verified. */
export async function resendVerification(email: string): Promise<void> {
  await apiFetch<{ ok: true }>('/api/auth/resend-verification', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}
